import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import EvolucaoLineChart from '../../components/EvolucaoLineChart';
import { temConteudo } from '../../lib/avaliacaoDetalhes';

const CAMPOS_FITA = [
  { key: 'peso',        label: 'Peso',        unidade: 'kg', placeholder: '68.50', step: '0.01' },
  { key: 'gordura',     label: '% Gordura',   unidade: '%',  placeholder: '24.5' },
  { key: 'cintura',     label: 'Cintura',     unidade: 'cm', placeholder: '72.0' },
  { key: 'abdomen',     label: 'Abdômen',     unidade: 'cm', placeholder: '80.0' },
  { key: 'quadril',     label: 'Quadril',     unidade: 'cm', placeholder: '98.0' },
  { key: 'braco',       label: 'Braço',       unidade: 'cm', placeholder: '30.0' },
  { key: 'coxa',        label: 'Coxa',        unidade: 'cm', placeholder: '58.0' },
  { key: 'panturrilha', label: 'Panturrilha', unidade: 'cm', placeholder: '36.0' },
];

const CAMPOS_RENPHO = [
  { key: 'bmi',                 label: 'BMI',                 unidade: '',     placeholder: '23.8' },
  { key: 'peso_sem_gordura',    label: 'Peso sem gordura',    unidade: 'kg',   placeholder: '45.60', step: '0.01' },
  { key: 'gordura_subcutanea',  label: 'Gordura subcutânea',  unidade: '%',    placeholder: '24.6' },
  { key: 'gordura_visceral',    label: 'Gordura visceral',    unidade: '',     placeholder: '7' },
  { key: 'liquido_corporal',    label: 'Líquido corporal',    unidade: '%',    placeholder: '50.0' },
  { key: 'musculo_esqueletico', label: 'Músculo esquelético', unidade: '%',    placeholder: '42.5' },
  { key: 'massa_muscular',      label: 'Massa muscular',      unidade: 'kg',   placeholder: '42.90', step: '0.01' },
  { key: 'massa_ossea',         label: 'Massa óssea',         unidade: 'kg',   placeholder: '2.74', step: '0.01' },
  { key: 'proteina',            label: 'Proteína',            unidade: '%',    placeholder: '17.1' },
  { key: 'bmr',                 label: 'BMR',                 unidade: 'kcal', placeholder: '1354' },
  { key: 'idade_metabolica',    label: 'Idade metabólica',    unidade: 'anos', placeholder: '41' },
];

const CAMPOS = [...CAMPOS_FITA, ...CAMPOS_RENPHO];

const CAMPOS_AVALIACAO_COMPLETA = {
  identificacao: [
    { key: 'altura', label: 'Altura' },
    { key: 'objetivo', label: 'Objetivo principal', amplo: true },
    { key: 'regiao_melhorar', label: 'Região a melhorar' },
    { key: 'proxima_avaliacao', label: 'Próxima avaliação', type: 'date' },
  ],
  anamnese: [
    { key: 'sono', label: 'Sono', amplo: true },
    { key: 'hidratacao', label: 'Hidratação', amplo: true },
    { key: 'alimentacao', label: 'Alimentação', amplo: true },
    { key: 'frequencia_treino', label: 'Frequência de treino' },
    { key: 'historico_lesoes', label: 'Histórico de lesões e desconfortos', amplo: true },
    { key: 'medicamentos', label: 'Medicamentos', amplo: true },
  ],
  perimetria: [
    { key: 'pescoco', label: 'Pescoço', unidade: 'cm', numero: true },
    { key: 'torax', label: 'Tórax', unidade: 'cm', numero: true },
    { key: 'abdomen_alto', label: 'Abdômen alto', unidade: 'cm', numero: true },
    { key: 'cintura', label: 'Cintura', unidade: 'cm', numero: true },
    { key: 'abdomen_baixo', label: 'Abdômen baixo', unidade: 'cm', numero: true },
    { key: 'biceps_direito', label: 'Bíceps direito', unidade: 'cm', numero: true },
    { key: 'biceps_esquerdo', label: 'Bíceps esquerdo', unidade: 'cm', numero: true },
    { key: 'quadril', label: 'Quadril', unidade: 'cm', numero: true },
    { key: 'coxa_direita', label: 'Coxa direita', unidade: 'cm', numero: true },
    { key: 'coxa_esquerda', label: 'Coxa esquerda', unidade: 'cm', numero: true },
    { key: 'panturrilha_direita', label: 'Panturrilha direita', unidade: 'cm', numero: true },
    { key: 'panturrilha_esquerda', label: 'Panturrilha esquerda', unidade: 'cm', numero: true },
  ],
  dobras: [
    { key: 'subescapular', label: 'Subescapular', unidade: 'mm', numero: true },
    { key: 'triceps', label: 'Tríceps', unidade: 'mm', numero: true },
    { key: 'peitoral', label: 'Peitoral', unidade: 'mm', numero: true },
    { key: 'axilar_media', label: 'Axilar média', unidade: 'mm', numero: true },
    { key: 'supra_iliaca', label: 'Supra-ilíaca', unidade: 'mm', numero: true },
    { key: 'abdomen', label: 'Abdômen', unidade: 'mm', numero: true },
    { key: 'coxa', label: 'Coxa', unidade: 'mm', numero: true },
    { key: 'somatoria', label: 'Somatória das dobras', unidade: 'mm', numero: true },
  ],
  postura: [
    { key: 'ombros', label: 'Ombros', amplo: true },
    { key: 'trapezio', label: 'Trapézio', amplo: true },
    { key: 'joelhos', label: 'Joelhos', amplo: true },
    { key: 'quadril', label: 'Quadril', amplo: true },
    { key: 'coluna', label: 'Coluna', amplo: true },
    { key: 'pes', label: 'Pés', amplo: true },
  ],
  testes: [
    { key: 'flexibilidade', label: 'Flexibilidade — Banco de Wells', amplo: true },
    { key: 'flexao_braco', label: 'Flexão de braço', amplo: true },
    { key: 'abdominal', label: 'Abdominal', amplo: true },
    { key: 'observacoes', label: 'Observações funcionais', amplo: true },
  ],
  sintese: [
    { key: 'bioimpedancia', label: 'Observação da bioimpedância', amplo: true },
    { key: 'perimetria', label: 'Observação da perimetria', amplo: true },
    { key: 'dobras', label: 'Observação das dobras', amplo: true },
    { key: 'impressao_clinica', label: 'Impressão clínica', amplo: true },
  ],
};

const FORM_VAZIO = () => ({
  data: new Date().toISOString().slice(0, 10),
  peso: '', gordura: '', cintura: '',
  abdomen: '', quadril: '', braco: '', coxa: '', panturrilha: '',
  bmi: '', peso_sem_gordura: '', gordura_subcutanea: '', gordura_visceral: '',
  liquido_corporal: '', musculo_esqueletico: '', massa_muscular: '',
  massa_ossea: '', proteina: '', bmr: '', idade_metabolica: '',
  observacoes: '',
});

function limparObjetoVazio(valor) {
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) return valor;
  return Object.fromEntries(Object.entries(valor)
    .map(([key, item]) => [key, limparObjetoVazio(item)])
    .filter(([, item]) => temConteudo(item)));
}

function arquivoParaBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function normalizarNumero(valor) {
  if (valor === null || valor === undefined || valor === '') return '';
  const match = String(valor).replace(',', '.').match(/-?\d+(\.\d+)?/);
  return match ? match[0] : '';
}

function Sparkline({ valores, cor = '#5B402E' }) {
  if (!valores || valores.length < 2) return null;
  const W = 80, H = 30;
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const range = max - min || 1;
  const pts = valores.map((v, i) => {
    const x = (i / (valores.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg width={W} height={H} style={{ display: 'block' }}>
      <polyline points={pts.join(' ')} fill="none" stroke={cor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1].split(',')[0]} cy={pts[pts.length - 1].split(',')[1]} r="3" fill={cor} />
    </svg>
  );
}

function SecaoRecolhivel({ titulo, descricao, aberta = false, children }) {
  return (
    <details className="evo-form-section" open={aberta}>
      <summary>
        <span>
          <strong>{titulo}</strong>
          {descricao && <small>{descricao}</small>}
        </span>
        <span className="evo-section-chevron" aria-hidden="true">⌄</span>
      </summary>
      <div className="evo-form-section-content">{children}</div>
    </details>
  );
}

function formatarMedida(valor, unidade = '') {
  if (valor === null || valor === undefined || valor === '') return '—';
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return `${valor}${unidade}`;
  const casas = Math.abs(numero * 10 - Math.round(numero * 10)) > 0.00001 ? 2 : 1;
  return `${numero.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}${unidade ? ` ${unidade}` : ''}`;
}

function ModalLancar({ aluna, onClose, onSaved }) {
  const [form, setForm] = useState(FORM_VAZIO());
  const [avaliacaoCompleta, setAvaliacaoCompleta] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(null);
  const [registros, setRegistros] = useState([]);
  const [loadingReg, setLoadingReg] = useState(true);
  const [indicadorGrafico, setIndicadorGrafico] = useState('peso');
  const [arquivoIA, setArquivoIA] = useState(null);
  const [lendoIA, setLendoIA] = useState(false);
  const [iaMensagem, setIaMensagem] = useState('');

  useEffect(() => {
    if (!aluna?.id) return;
    setLoadingReg(true);
    supabase.from('evolucao_fisica').select('*').eq('aluna_id', aluna.id).order('data', { ascending: false })
      .then(({ data }) => { setRegistros(data || []); setLoadingReg(false); });
  }, [aluna]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleDetalheChange = (secao, key, value) => {
    setAvaliacaoCompleta(atual => {
      const proximaSecao = { ...(atual[secao] || {}), [key]: value };
      if (secao === 'dobras' && key !== 'somatoria') {
        const chaves = CAMPOS_AVALIACAO_COMPLETA.dobras.filter(c => c.key !== 'somatoria').map(c => c.key);
        const valores = chaves.map(chave => Number(String(proximaSecao[chave] || '').replace(',', '.'))).filter(Number.isFinite);
        proximaSecao.somatoria = valores.length ? valores.reduce((soma, numero) => soma + numero, 0).toFixed(1) : '';
      }
      return { ...atual, [secao]: proximaSecao };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!form.data) { setErro('Informe a data.'); return; }
    setSalvando(true);
    const detalhesLimpos = limparObjetoVazio(avaliacaoCompleta);
    const payload = {
      aluna_id: aluna.id,
      data: form.data,
      observacoes: form.observacoes || null,
      detalhes: temConteudo(detalhesLimpos) ? detalhesLimpos : null,
      registrado_por: 'admin',
    };
    CAMPOS.forEach(c => { payload[c.key] = form[c.key] !== '' ? parseFloat(form[c.key]) : null; });
    let result;
    if (editando) {
      result = await supabase.from('evolucao_fisica').update(payload).eq('id', editando);
    } else {
      result = await supabase.from('evolucao_fisica').insert(payload);
    }
    setSalvando(false);
    if (result.error) {
      setErro('Erro ao salvar: ' + result.error.message);
    } else {
      const { data } = await supabase.from('evolucao_fisica').select('*').eq('aluna_id', aluna.id).order('data', { ascending: false });
      setRegistros(data || []);
      setForm(FORM_VAZIO());
      setAvaliacaoCompleta({});
      setEditando(null);
      onSaved?.();
    }
  };

  const handleEditar = (reg) => {
    setEditando(reg.id);
    const proximo = FORM_VAZIO();
    proximo.data = reg.data || proximo.data;
    CAMPOS.forEach(c => { proximo[c.key] = reg[c.key] ?? ''; });
    proximo.observacoes = reg.observacoes ?? '';
    setForm(proximo);
    setAvaliacaoCompleta(reg.detalhes || {});
  };

  const preencherComIA = async () => {
    if (!arquivoIA) return;
    setErro('');
    setIaMensagem('');
    setLendoIA(true);
    try {
      const base64 = await arquivoParaBase64(arquivoIA);
      const mediaType = arquivoIA.type || (arquivoIA.name?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/png');
      const response = await fetch('/.netlify/functions/extrair-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'evolucao', fileBase64: base64, mediaType })
      });
      const dados = await response.json();
      if (!response.ok) throw new Error(dados.error || 'Não foi possível ler este arquivo.');

      const valoresExtraidos = {};
      CAMPOS.forEach(c => {
        const valor = normalizarNumero(dados[c.key]);
        if (valor !== '') valoresExtraidos[c.key] = valor;
      });
      const preenchidos = Object.keys(valoresExtraidos).length;
      setForm(f => {
        const proximo = { ...f };
        if (dados.data) proximo.data = dados.data;
        Object.entries(valoresExtraidos).forEach(([key, valor]) => { proximo[key] = valor; });
        if (dados.observacoes) {
          proximo.observacoes = [f.observacoes, dados.observacoes].filter(Boolean).join('\n');
        }
        return proximo;
      });
      const detalhesExtraidos = limparObjetoVazio(dados.avaliacao_completa || {});
      setAvaliacaoCompleta(detalhesExtraidos);
      const secoesExtras = Object.values(detalhesExtraidos).filter(temConteudo).length;
      setIaMensagem(preenchidos > 0
        ? `A IA preencheu ${preenchidos} campo${preenchidos > 1 ? 's' : ''}${secoesExtras ? ` e encontrou ${secoesExtras} seção${secoesExtras > 1 ? 'ões' : ''} da avaliação completa` : ''}. Revise antes de salvar.`
        : 'A IA leu o arquivo, mas não encontrou medidas claras. Você pode preencher manualmente.');
    } catch (err) {
      setErro('Erro ao ler com IA: ' + (err.message || 'tente outro PDF ou print.'));
    } finally {
      setLendoIA(false);
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir este registro?')) return;
    await supabase.from('evolucao_fisica').delete().eq('id', id);
    setRegistros(r => r.filter(x => x.id !== id));
    onSaved?.();
  };

  const formatData = (str) => {
    const d = new Date(str + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const inputStyle = { width: '100%', border: '1.5px solid rgba(216,202,182,.9)', borderRadius: '18px', padding: '10px 13px', fontSize: '14px', color: '#251C19', background: 'rgba(255,255,255,.82)', outline: 'none', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };
  const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 700, color: '#4A3728', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(37,28,25,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        .evo-modal-card {
          background: rgba(255,255,255,.78);
          border: 1px solid rgba(216,202,182,.72);
          border-radius: 28px;
          box-shadow: 0 16px 38px rgba(82,69,57,.06), inset 0 1px 0 rgba(255,255,255,.92);
        }
        .evo-modal-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(300px, .85fr);
          gap: 18px;
          padding: 20px;
        }
        .evo-section-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: #251C19;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: .08em;
          text-transform: uppercase;
          margin: 18px 0 12px;
        }
        .evo-field-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(142px, 1fr));
          gap: 12px;
        }
        .evo-form-section {
          background: rgba(255,255,255,.62);
          border: 1px solid rgba(216,202,182,.82);
          border-radius: 20px;
          margin-bottom: 12px;
          overflow: hidden;
        }
        .evo-form-section > summary {
          list-style: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 15px 16px;
          color: #251C19;
          user-select: none;
        }
        .evo-form-section > summary::-webkit-details-marker { display: none; }
        .evo-form-section > summary strong {
          display: block;
          font-size: 13px;
          letter-spacing: .03em;
        }
        .evo-form-section > summary small {
          display: block;
          color: #8B7355;
          font-size: 11px;
          font-weight: 500;
          margin-top: 3px;
        }
        .evo-section-chevron {
          color: #8B7355;
          font-size: 18px;
          transition: transform .2s ease;
        }
        .evo-form-section[open] .evo-section-chevron { transform: rotate(180deg); }
        .evo-form-section-content {
          border-top: 1px solid rgba(216,202,182,.65);
          padding: 16px;
        }
        .evo-current-summary {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          margin-bottom: 16px;
        }
        .evo-current-summary > div {
          background: #F3EEE6;
          border-radius: 16px;
          padding: 10px 12px;
        }
        .evo-current-summary small {
          display: block;
          color: #8B7355;
          font-size: 10px;
          margin-bottom: 3px;
          text-transform: uppercase;
          letter-spacing: .04em;
        }
        .evo-current-summary strong { color: #4A3728; font-size: 14px; }
        .evo-history-details summary {
          cursor: pointer;
          color: #8B7355;
          font-size: 11px;
          font-weight: 700;
          margin-top: 7px;
        }
        .evo-history-measures {
          display: flex;
          flex-wrap: wrap;
          gap: 7px 10px;
          padding-top: 8px;
          font-size: 11px;
          color: #8B7355;
        }
        .evo-sticky-actions {
          position: sticky;
          bottom: 0;
          padding-top: 14px;
          background: linear-gradient(180deg, rgba(250,247,242,0), #FAF7F2 36%);
        }
        @media (max-width: 980px) {
          .evo-modal-grid { grid-template-columns: 1fr; padding: 16px; }
        }
        @media (max-width: 560px) {
          .evo-current-summary { grid-template-columns: 1fr; }
        }
      `}</style>
      <div style={{ background: '#FAF7F2', borderRadius: '32px', width: '100%', maxWidth: '980px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden', marginTop: '20px', marginBottom: '20px' }}>
        <div style={{ background: 'linear-gradient(135deg, #251C19, #3A2E27)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#FAF7F2', fontSize: '17px', fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>Evolução Física</div>
            <div style={{ color: '#E3DBC7', fontSize: '13px', marginTop: '2px' }}>{aluna?.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#FAF7F2', fontSize: '18px', cursor: 'pointer', borderRadius: '20px', width: '32px', height: '32px' }}>×</button>
        </div>

        <div className="evo-modal-grid">
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#251C19', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span>{editando ? 'Editando registro' : 'Novo registro'}</span>
              {editando && <button onClick={() => { setEditando(null); setForm(FORM_VAZIO()); setAvaliacaoCompleta({}); }} style={{ fontSize: '11px', color: '#8B7355', background: 'rgba(255,255,255,.78)', border: '1px solid #E3DBC7', borderRadius: '20px', padding: '5px 10px', cursor: 'pointer' }}>Cancelar edição</button>}
            </div>

          <div className="evo-modal-card" style={{ padding: '15px', marginBottom: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#251C19', marginBottom: '3px' }}>Ler PDF ou print com IA</div>
                <div style={{ fontSize: '12px', color: '#8B7355', lineHeight: 1.4 }}>Envie avaliação, bioimpedância ou print. A IA preenche os campos e você revisa antes de salvar.</div>
              </div>
              <button
                type="button"
                onClick={preencherComIA}
                disabled={!arquivoIA || lendoIA}
                style={{ background: !arquivoIA || lendoIA ? '#CDBFAE' : '#5B402E', color: 'white', border: 'none', borderRadius: '20px', padding: '9px 14px', fontSize: '12px', fontWeight: 700, cursor: !arquivoIA || lendoIA ? 'not-allowed' : 'pointer', flexShrink: 0 }}
              >
                {lendoIA ? 'Lendo...' : 'Preencher'}
              </button>
            </div>
            <input
              type="file"
              accept=".pdf,application/pdf,image/*"
              onChange={e => { setArquivoIA(e.target.files?.[0] || null); setIaMensagem(''); }}
              style={{ marginTop: '12px', width: '100%', color: '#8B7355', fontSize: '12px' }}
            />
            {iaMensagem && <div style={{ marginTop: '10px', background: '#F3F7EC', border: '1px solid #DBE8C9', color: '#5C6F3B', borderRadius: '18px', padding: '9px 12px', fontSize: '12px' }}>{iaMensagem}</div>}
          </div>

          <form onSubmit={handleSubmit}>
            {registros[0] && !editando && (
              <div className="evo-current-summary" aria-label="Resumo da avaliação mais recente">
                <div><small>Última avaliação</small><strong>{formatData(registros[0].data)}</strong></div>
                <div><small>Peso</small><strong>{formatarMedida(registros[0].peso, 'kg')}</strong></div>
                <div><small>Gordura corporal</small><strong>{formatarMedida(registros[0].gordura, '%')}</strong></div>
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Data da avaliação</label>
              <input type="date" name="data" value={form.data} onChange={handleChange} required style={inputStyle} />
            </div>
            <SecaoRecolhivel titulo="Medidas principais" descricao="Peso, gordura e medidas feitas com fita" aberta>
              <div className="evo-field-grid">
                {CAMPOS_FITA.map(c => (
                  <div key={c.key}>
                    <label style={labelStyle}>{c.label} <span style={{ color: '#A8937A', fontWeight: 500 }}>{c.unidade}</span></label>
                    <input type="number" name={c.key} value={form[c.key]} onChange={handleChange} placeholder={c.placeholder} step={c.step || '0.1'} min="0" style={inputStyle} />
                  </div>
                ))}
              </div>
            </SecaoRecolhivel>
            <SecaoRecolhivel titulo="Bioimpedância" descricao="Dados importados da Renpho ou preenchidos manualmente">
              <div className="evo-field-grid">
                {CAMPOS_RENPHO.map(c => (
                  <div key={c.key}>
                    <label style={labelStyle}>{c.label} <span style={{ color: '#A8937A', fontWeight: 500 }}>{c.unidade}</span></label>
                    <input type="number" name={c.key} value={form[c.key]} onChange={handleChange} placeholder={c.placeholder} step={c.step || '0.1'} min="0" style={inputStyle} />
                  </div>
                ))}
              </div>
            </SecaoRecolhivel>
            {[
              ['identificacao', 'Objetivo e planejamento', 'Dados encontrados na avaliação completa'],
              ['anamnese', 'Anamnese', 'Rotina, saúde e informações relevantes'],
              ['perimetria', 'Perimetria completa', 'Medidas detalhadas e comparação entre os lados'],
              ['dobras', 'Dobras cutâneas', 'Protocolo Pollock e somatória automática'],
              ['postura', 'Avaliação postural', 'Registros de alinhamento e compensações'],
              ['testes', 'Avaliação funcional e testes', 'Resultados realizados e observações'],
              ['sintese', 'Síntese profissional', 'Observações técnicas e impressão clínica'],
            ].map(([secao, titulo, descricao]) => temConteudo(avaliacaoCompleta[secao]) && (
              <SecaoRecolhivel key={secao} titulo={titulo} descricao={descricao}>
                <div className="evo-field-grid">
                  {CAMPOS_AVALIACAO_COMPLETA[secao].map(campo => {
                    const valor = avaliacaoCompleta[secao]?.[campo.key] ?? '';
                    return (
                      <div key={campo.key} style={campo.amplo ? { gridColumn: '1 / -1' } : undefined}>
                        <label style={labelStyle}>{campo.label} {campo.unidade && <span style={{ color: '#A8937A', fontWeight: 500 }}>{campo.unidade}</span>}</label>
                        {campo.amplo ? (
                          <textarea
                            value={valor}
                            onChange={e => handleDetalheChange(secao, campo.key, e.target.value)}
                            rows={2}
                            style={{ ...inputStyle, resize: 'vertical', minHeight: '64px' }}
                          />
                        ) : (
                          <input
                            type={campo.type || (campo.numero ? 'number' : 'text')}
                            value={valor}
                            onChange={e => handleDetalheChange(secao, campo.key, e.target.value)}
                            step={campo.numero ? '0.01' : undefined}
                            min={campo.numero ? '0' : undefined}
                            readOnly={secao === 'dobras' && campo.key === 'somatoria'}
                            style={{ ...inputStyle, background: secao === 'dobras' && campo.key === 'somatoria' ? '#F3EEE6' : inputStyle.background }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </SecaoRecolhivel>
            ))}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Observações (opcional)</label>
              <textarea name="observacoes" value={form.observacoes} onChange={handleChange} placeholder="Ex: aluna relatou retenção hídrica..." rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} />
            </div>
            {erro && <div style={{ background: '#FEF0ED', border: '1px solid #FBCFC8', color: '#C0392B', borderRadius: '20px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>{erro}</div>}
            <div className="evo-sticky-actions">
              <button type="submit" disabled={salvando} style={{ width: '100%', background: salvando ? '#A8937A' : '#5B402E', color: 'white', border: 'none', borderRadius: '24px', padding: '14px', fontSize: '14px', fontWeight: 700, cursor: salvando ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: salvando ? 'none' : '0 12px 26px rgba(91,64,46,.18)' }}>
                {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Salvar registro'}
              </button>
            </div>
          </form>
          </div>

          <div>
        {registros.length >= 2 && (
          <div style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#251C19' }}>Gráfico de evolução</div>
              <select value={indicadorGrafico} onChange={e => setIndicadorGrafico(e.target.value)} style={{ border: '1.5px solid #E3DBC7', borderRadius: '20px', padding: '6px 12px', fontSize: '12px', color: '#251C19', background: 'white', outline: 'none', fontFamily: 'Inter, sans-serif' }}>
                {CAMPOS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="evo-modal-card" style={{ padding: '16px 12px 8px' }}>
              <EvolucaoLineChart dados={[...registros].reverse()} indicador={indicadorGrafico} cor="#5B402E" unidade={CAMPOS.find(c => c.key === indicadorGrafico)?.unidade || ''} />
            </div>
          </div>
        )}

        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#251C19', marginBottom: '12px' }}>
            Histórico ({registros.length} registros)
          </div>
          {loadingReg ? <div style={{ color: '#8B7355', fontSize: '13px' }}>Carregando...</div>
          : registros.length === 0 ? <div className="evo-modal-card" style={{ textAlign: 'center', padding: '24px', color: '#8B7355', fontSize: '13px', borderStyle: 'dashed' }}>Nenhum registro ainda.</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '58vh', overflowY: 'auto', paddingRight: '2px' }}>
            {registros.map(reg => (
              <div key={reg.id} className="evo-modal-card" style={{ border: editando === reg.id ? '2px solid #5B402E' : undefined, padding: '13px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#251C19', marginBottom:'3px', display:'flex', alignItems:'center', gap:'6px' }}>
                    {formatData(reg.data)}
                    <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'100px', background: reg.registrado_por==='aluna'?'rgba(137,91,64,0.08)':'rgba(46,125,50,0.08)', color: reg.registrado_por==='aluna'?'#5B402E':'#2E7D32', fontWeight:600 }}>
                      {reg.registrado_por==='aluna'?'aluna':'equipe'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#8B7355', marginTop: '3px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {['peso', 'gordura', 'cintura'].map(key => {
                      const c = CAMPOS.find(campo => campo.key === key);
                      return reg[key] != null && <span key={key}>{c.label}: <strong style={{ color: '#4A3728' }}>{formatarMedida(reg[key], c.unidade)}</strong></span>;
                    })}
                  </div>
                  {CAMPOS.some(c => !['peso', 'gordura', 'cintura'].includes(c.key) && reg[c.key] != null) && (
                    <details className="evo-history-details">
                      <summary>Ver todas as medidas</summary>
                      <div className="evo-history-measures">
                        {CAMPOS.filter(c => !['peso', 'gordura', 'cintura'].includes(c.key)).map(c =>
                          reg[c.key] != null && <span key={c.key}>{c.label}: <strong style={{ color: '#4A3728' }}>{formatarMedida(reg[c.key], c.unidade)}</strong></span>
                        )}
                      </div>
                    </details>
                  )}
                  {reg.observacoes && <div style={{ fontSize: '11px', color: '#A8937A', marginTop: '4px', fontStyle: 'italic' }}>{reg.observacoes}</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => handleEditar(reg)} style={{ background: '#F5F0E8', border: 'none', borderRadius: '20px', padding: '7px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: '#5B402E' }}>Editar</button>
                  <button onClick={() => handleExcluir(reg.id)} style={{ background: '#FEF0ED', border: 'none', borderRadius: '20px', padding: '7px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: '#A85340' }}>Excluir</button>
                </div>
              </div>
            ))}
          </div>}
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EvolucaoAluna() {
  const { alunas, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const alunaIdParam = searchParams.get('aluna');
  const [busca, setBusca] = useState('');
  const [modalAluna, setModalAluna] = useState(null);
  const [resumos, setResumos] = useState({});

  useEffect(() => {
    if (!alunaIdParam || !alunas.length) return;
    const aluna = alunas.find(a => a.id === alunaIdParam);
    if (aluna) setModalAluna(aluna);
  }, [alunaIdParam, alunas]);

  useEffect(() => {
    const carregar = async () => {
      const { data } = await supabase.from('evolucao_fisica').select('aluna_id, data, peso').order('data', { ascending: true });
      if (!data) return;
      const mapa = {};
      data.forEach(r => { if (!mapa[r.aluna_id]) mapa[r.aluna_id] = []; mapa[r.aluna_id].push(r); });
      const resultado = {};
      Object.entries(mapa).forEach(([id, regs]) => {
        const pesos = regs.map(r => r.peso).filter(v => v != null);
        resultado[id] = { total: regs.length, ultimaData: regs[regs.length - 1]?.data, ultimoPeso: regs[regs.length - 1]?.peso, seriePeso: pesos };
      });
      setResumos(resultado);
    };
    carregar();
  }, []);

  const alunasFiltradas = (alunas || []).filter(a => (a.name || '').toLowerCase().includes(busca.toLowerCase()));

  const formatData = (str) => {
    if (!str) return '—';
    return new Date(str + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const recarregarResumos = () => {
    supabase.from('evolucao_fisica').select('aluna_id, data, peso').order('data', { ascending: true }).then(({ data }) => {
      if (!data) return;
      const mapa = {};
      data.forEach(r => { if (!mapa[r.aluna_id]) mapa[r.aluna_id] = []; mapa[r.aluna_id].push(r); });
      const resultado = {};
      Object.entries(mapa).forEach(([id, regs]) => {
        const pesos = regs.map(r => r.peso).filter(v => v != null);
        resultado[id] = { total: regs.length, ultimaData: regs[regs.length - 1]?.data, ultimoPeso: regs[regs.length - 1]?.peso, seriePeso: pesos };
      });
      setResumos(resultado);
    });
  };

  return (
    <AdminLayout>
      <div style={{ fontFamily: 'Inter, sans-serif' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: '34px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Evolução Física</h1>
          <p style={{ color: '#8B7355', margin: '4px 0 0', fontSize: '14px' }}>Acompanhe e lance medidas de cada aluna</p>
        </div>
        <div style={{ marginBottom: '20px', maxWidth: '360px' }}>
          <input type="text" placeholder="Buscar aluna..." value={busca} onChange={e => setBusca(e.target.value)} style={{ width: '100%', border: '1.5px solid #E3DBC7', borderRadius: '24px', padding: '10px 14px', fontSize: '14px', color: '#251C19', background: 'white', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#8B7355' }}>Carregando alunas...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
            {alunasFiltradas.map(aluna => {
              const res = resumos[aluna.id];
              return (
                <div key={aluna.id}
                  style={{ background: 'white', border: '1.5px solid #E8E0D0', borderRadius: '24px', padding: '18px', cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                  onClick={() => setModalAluna(aluna)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#5B402E'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(137,91,64,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E0D0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '15px', fontWeight: 600, color: '#251C19', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{aluna.name}</div>
                      {res ? (
                        <>
                          <div style={{ fontSize: '12px', color: '#8B7355', marginBottom: '2px' }}>{res.total} registro{res.total !== 1 ? 's' : ''} · última: {formatData(res.ultimaData)}</div>
                          {res.ultimoPeso && <div style={{ fontSize: '13px', color: '#5B402E', fontWeight: 600 }}>{parseFloat(res.ultimoPeso).toFixed(1)} kg</div>}
                        </>
                      ) : (
                        <div style={{ fontSize: '12px', color: '#B0A090' }}>Sem registros</div>
                      )}
                    </div>
                    {res?.seriePeso?.length >= 2 && <div style={{ flexShrink: 0 }}><Sparkline valores={res.seriePeso} /></div>}
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#5B402E', fontWeight: 600 }}>
                    {res ? '✏️ Ver e editar medidas' : '➕ Lançar primeira medição'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && alunasFiltradas.length === 0 && <div style={{ textAlign: 'center', padding: '48px', color: '#8B7355' }}>Nenhuma aluna encontrada.</div>}
        {modalAluna && <ModalLancar aluna={modalAluna} onClose={() => setModalAluna(null)} onSaved={recarregarResumos} />}
      </div>
    </AdminLayout>
  );
}
